import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { adminsService } from '../../services/adminsService';
import { AdminResponse, AdminType, CreateAdminRequest } from '../../types/auth';
import CreateAdminModal from '../modals/CreateAdminModal';
import './Page.css';

const Admins: React.FC = () => {
  const { t } = useTranslation();
  const [admins, setAdmins] = useState<AdminResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  const [deletingAdminId, setDeletingAdminId] = useState<number | null>(null);

  const fetchAdmins = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await adminsService.getAllAdmins();

      if (response.success && response.data) {
        setAdmins(response.data);
      } else {
        setError(response.error || 'Failed to fetch admins');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const handleCreateAdmin = async (request: CreateAdminRequest) => {
    setCreatingAdmin(true);
    setError('');

    try {
      const response = await adminsService.createAdmin(request);

      if (response.success) {
        setSuccessMessage(t('admins.adminCreated'));
        setIsCreateModalOpen(false);
        // Refresh admin list
        await fetchAdmins();
        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(response.error || 'Failed to create admin');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setCreatingAdmin(false);
    }
  };

  const handleDeleteAdmin = async (adminId: number, adminName: string, adminType: AdminType) => {
    // Prevent deleting super admins
    if (adminType === AdminType.SUPER_ADMIN) {
      setError(t('admins.cannotDeleteSuperAdmin'));
      setTimeout(() => setError(''), 3000);
      return;
    }

    if (!window.confirm(t('admins.deleteConfirm', { name: adminName }))) {
      return;
    }

    setDeletingAdminId(adminId);
    setError('');

    try {
      const response = await adminsService.deleteAdmin(adminId);

      if (response.success) {
        setSuccessMessage(t('admins.adminDeleted'));
        // Refresh admin list
        await fetchAdmins();
        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(response.error || 'Failed to delete admin');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setDeletingAdminId(null);
    }
  };

  const getAdminTypeBadge = (adminType: AdminType) => {
    const isSuper = adminType === AdminType.SUPER_ADMIN;
    return (
      <span
        style={{
          display: 'inline-block',
          padding: '0.25rem 0.75rem',
          borderRadius: '12px',
          fontSize: '0.85rem',
          fontWeight: 500,
          backgroundColor: isSuper ? '#6f42c1' : '#17a2b8',
          color: 'white'
        }}
      >
        {isSuper ? t('navbar.superAdmin') : t('navbar.admin')}
      </span>
    );
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1>{t('admins.title')}</h1>
        <p>{t('admins.subtitle')}</p>
      </div>

      <div className="page-content">
        {error && (
          <div className="error-message" style={{ marginBottom: '1.5rem' }}>
            {error}
          </div>
        )}

        {successMessage && (
          <div style={{
            padding: '1rem',
            backgroundColor: '#d4edda',
            color: '#155724',
            border: '1px solid #c3e6cb',
            borderRadius: '4px',
            marginBottom: '1.5rem'
          }}>
            {successMessage}
          </div>
        )}

        <div style={{ marginBottom: '1.5rem' }}>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="search-button"
            style={{ padding: '0.75rem 1.5rem' }}
          >
            + {t('admins.createAdmin')}
          </button>
        </div>

        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('admins.adminId')}</th>
                <th>{t('common.name')}</th>
                <th>{t('common.phone')}</th>
                <th>{t('common.email')}</th>
                <th>{t('admins.adminType')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="no-data">{t('admins.loadingAdmins')}</td>
                </tr>
              ) : admins.length === 0 ? (
                <tr>
                  <td colSpan={6} className="no-data">{t('admins.noAdmins')}</td>
                </tr>
              ) : (
                admins.map((admin) => (
                  <tr key={admin.id}>
                    <td>#{admin.id}</td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{admin.name}</div>
                    </td>
                    <td>{admin.phone}</td>
                    <td>{admin.email}</td>
                    <td>{getAdminTypeBadge(admin.adminType)}</td>
                    <td>
                      <button
                        onClick={() => handleDeleteAdmin(admin.id, admin.name, admin.adminType)}
                        disabled={deletingAdminId === admin.id || admin.adminType === AdminType.SUPER_ADMIN}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: admin.adminType === AdminType.SUPER_ADMIN ? '#6c757d' : '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: (deletingAdminId === admin.id || admin.adminType === AdminType.SUPER_ADMIN) ? 'not-allowed' : 'pointer',
                          opacity: (deletingAdminId === admin.id || admin.adminType === AdminType.SUPER_ADMIN) ? 0.6 : 1,
                          fontSize: '0.9rem'
                        }}
                        title={admin.adminType === AdminType.SUPER_ADMIN ? t('admins.cannotDeleteSuperAdmin') : ''}
                      >
                        {deletingAdminId === admin.id ? t('admins.deleting') : t('admins.deleteButton')}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {admins.length > 0 && (
          <div style={{ marginTop: '1rem', color: '#666', fontSize: '0.9rem' }}>
            {t('admins.totalAdmins')} <strong>{admins.length}</strong>
          </div>
        )}
      </div>

      <CreateAdminModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateAdmin}
        loading={creatingAdmin}
      />
    </div>
  );
};

export default Admins;
